import React from 'react';
import {
  Badge,
  BedroomChild,
  BorderColor,
  CalendarMonth,
  CorporateFare,
  DateRange,
  DriveFileMove,
  FactCheck,
  Person,
  Task
} from '@mui/icons-material';
import ContactPageIcon from '@mui/icons-material/ContactPage';
import ScreenSearchDesktopIcon from '@mui/icons-material/ScreenSearchDesktop';

import {
  ScreenerSettingName,
  useIsScreenerSettingEnabled
} from '@/hooks/screener-settings';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useSelectedFacilityId } from '@/hooks/useFacilitiesQuery';
import { useCrmEsignAvailability } from '@/pages/CRM/Screener/hooks/useCrmEsignAvailability';
import { useIsBedBoardEnabled } from '@/pages/CRM/Screener/hooks/useIsBedBoardEnabled';
import { useIsReferralOutboundEnabled } from '@/pages/CRM/Screener/hooks/useIsReferralOutboundEnabled';
import { useIsScreenerLeadWaitlistEnabled } from '@/pages/CRM/Screener/hooks/useIsScreenerLeadWaitlistEnabled';
import { RoutePath } from '@/routes/RoutePath';
import { ExacareFeature, FeatureFlagService } from '@/utils/featureFlagService';
import { generatePathWithDomain } from '@/utils/generatePathWithDomain';

export interface SidebarItem {
  icon: React.ReactNode;
  text: string;
  path: string;
  activePaths?: string[];
  showOnMobile?: boolean;
  onClick?: () => void;
}

export const useGetSidebarCRMItems = (): SidebarItem[] => {
  const selectedFacilityId = useSelectedFacilityId();
  const currentUser = useCurrentUser().data!;
  const isBedBoardEnabled = useIsBedBoardEnabled();
  const isReferralOutboundEnabled = useIsReferralOutboundEnabled();
  const isCalendarEnabled = useIsScreenerSettingEnabled(
    ScreenerSettingName.ScreenerCalendar,
    selectedFacilityId ?? ''
  );
  const isWaitlistEnabled = useIsScreenerLeadWaitlistEnabled();
  const { isEnabled: isCrmEsignEnabled } = useCrmEsignAvailability();

  const preAuthSetting = useIsScreenerSettingEnabled(
    ScreenerSettingName.PalPreauth,
    selectedFacilityId as string
  );

  const isAuthorizationsEnabled =
    FeatureFlagService.isEnabled(
      ExacareFeature.CRM_SCREENER_PAL_PRE_AUTH_AUTHORIZATIONS_TABLE
    ) && preAuthSetting;

  return [
    ...(FeatureFlagService.isEnabled(ExacareFeature.CRM_SCREENER)
      ? [
          {
            icon: <ScreenSearchDesktopIcon />,
            text: 'Screener',
            path: RoutePath.CRMScreenerAllLeads,
            showOnMobile: true
          }
        ]
      : []),
    ...(isAuthorizationsEnabled
      ? [
          {
            icon: <FactCheck />,
            text: 'Authorizations',
            path: RoutePath.CRMAuthorizations,
            showOnMobile: true
          }
        ]
      : []),
    ...(isWaitlistEnabled
      ? [
          {
            icon: <ContactPageIcon />,
            text: 'Waitlist',
            path: RoutePath.CRMWaitlist,
            showOnMobile: true
          }
        ]
      : []),
    ...(isBedBoardEnabled
      ? [
          {
            icon: <Person />,
            text: 'Residents',
            path: RoutePath.CrmScreenerResidentsAllResidents,
            showOnMobile: true
          }
        ]
      : []),
    ...(currentUser.isScreenerCustomer() && isBedBoardEnabled
      ? [
          {
            icon: <BedroomChild />,
            text: 'Bed Board',
            path: RoutePath.CRMBedboard,
            showOnMobile: true
          }
        ]
      : []),
    ...(isReferralOutboundEnabled
      ? [
          {
            icon: <DriveFileMove />,
            text: 'Outbound',
            path: RoutePath.CRMReferralOutbound,
            showOnMobile: true
          }
        ]
      : []),
    ...(isReferralOutboundEnabled && isCalendarEnabled
      ? [
          {
            icon: <CalendarMonth />,
            text: 'Calendar',
            path: RoutePath.CRMReferralOutboundCalendar,
            showOnMobile: true
          }
        ]
      : []),
    ...(currentUser.isScreenerCustomer() &&
    FeatureFlagService.isEnabled(ExacareFeature.CRM_SCREENER_TASKS)
      ? [
          {
            icon: <DateRange />,
            text: 'Tasks',
            path: RoutePath.CRMTasks,
            showOnMobile: true
          }
        ]
      : currentUser.isScreenerCustomer()
        ? []
        : [
            {
              icon: <DateRange />,
              text: 'Tasks',
              path: RoutePath.CRMTasks,
              showOnMobile: true
            }
          ]),
    ...(isCrmEsignEnabled
      ? [
          {
            icon: <BorderColor />,
            text: 'eSign',
            path: RoutePath.CRMScreenerESign,
            activePaths: [
              RoutePath.CRMScreenerESign,
              RoutePath.CRMScreenerSingleLeadESignNew,
              RoutePath.CRMScreenerSingleLeadESignRequest
            ],
            showOnMobile: true
          }
        ]
      : []),

    ...(currentUser.isScreenerCustomer()
      ? [
          {
            icon: <Badge />,
            text: 'Sources',
            path: RoutePath.CRMSources,
            showOnMobile: true
          }
        ]
      : []),
    ...(FeatureFlagService.isEnabled(ExacareFeature.DATA_CENTER_TAB) &&
    currentUser.isDataCenterEnabled()
      ? [
          {
            icon: <Task />,
            text: 'Data Center',
            path: generatePathWithDomain(RoutePath.DataCenterDashboards)
          }
        ]
      : []),
    ...(currentUser.hasMinimumUserRole('L1')
      ? [
          {
            icon: <CorporateFare />,
            text: 'Admin Hub',
            path: generatePathWithDomain(RoutePath.AdminHub)
          }
        ]
      : [])
  ];
};
